import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  /** @type {Map<HTMLInputElement | HTMLButtonElement | HTMLSelectElement, boolean>} */
  #disabledControls = new Map();

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    this.#disableCartItems(line);

    const lineKey = cartItemRowToRemove.dataset.key;
    const removedItem = await this.fetchCartData()
      .then((cart) => cart.items?.find((item) => item.key === lineKey) ?? cart.items?.[line - 1] ?? null)
      .catch((error) => {
        console.warn('[cart-items] Could not prepare item restoration:', error);
        return null;
      });

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      removedItem,
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Keep the rows in the DOM until Shopify confirms the removal. This makes
    // errors recoverable while preserving immediate visual feedback.
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) return;

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {Object | null} [config.removedItem] - Ajax cart item used to restore a removed line.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems(config.line);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    let mutationSucceeded = false;
    let confirmedCart = null;

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          const currentCart = await this.fetchCartData().catch(() => null);
          deferredUpdatePromise.resolve({
            cart: currentCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart) : null,
            detail: {
              items: currentCart?.items,
              itemCount: currentCart?.item_count,
              source: 'cart-items-component',
              didError: true,
            },
          });
          return;
        }

        mutationSucceeded = true;
        confirmedCart = parsedResponseText;

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        let newCartItemCount = Number(parsedResponseText.item_count ?? 0);

        if (sectionHTML) {
          const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;

          if (newCartHiddenItemCount) {
            newCartItemCount = parseInt(newCartHiddenItemCount, 10);
          }
        }

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        try {
          await this.#renderConfirmedCartSection(sectionHTML, newCartItemCount === 0);
        } catch (error) {
          const currentCart = await this.fetchCartData().catch(() => parsedResponseText);
          confirmedCart = currentCart;
          newCartItemCount = Number(currentCart?.item_count ?? newCartItemCount);
          this.#updateQuantitySelectors(currentCart);
          this.#markSectionRefreshNeeded();
          this.#handleCartRefreshError(line, error);
        }

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(confirmedCart),
          detail: {
            sections: parsedResponseText.sections,
            items: confirmedCart?.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
            didReconcile: !document
              .getElementById(`shopify-section-${this.sectionId}`)
              ?.hasAttribute('data-cart-needs-refresh'),
          },
        });

        this.#updateCartQuantitySelectorButtonStates();

        if (quantity === 0 && config.removedItem) this.#announceRemovedItem(config.removedItem);
      })
      .catch(async (error) => {
        console.warn(
          mutationSucceeded ? '[cart-items] Cart view reconciliation failed:' : '[cart-items] Cart update failed:',
          error
        );
        resetShimmer(this);

        if (mutationSucceeded) {
          const currentCart = await this.fetchCartData().catch(() => confirmedCart);
          if (currentCart) this.#updateQuantitySelectors(currentCart);
          this.#markSectionRefreshNeeded();
          this.#handleCartRefreshError(line, error);
          deferredUpdatePromise.resolve({
            cart: currentCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart) : null,
            detail: {
              sections: confirmedCart?.sections,
              items: currentCart?.items,
              itemCount: currentCart?.item_count,
              source: 'cart-items-component',
              didError: false,
              didReconcile: false,
            },
          });
          if (quantity === 0 && config.removedItem) this.#announceRemovedItem(config.removedItem);
          return;
        }

        deferredUpdatePromise.reject(error);
        this.#handleCartError(line, {
          errors: this.dataset.updateError || error?.message || 'Failed to update cart',
        });
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Reconciles a confirmed cart mutation with the rendered section. A fresh
   * Section Rendering request is retried once because Shopify can omit or
   * temporarily fail a requested section even when the mutation succeeded.
   * @param {string | undefined} sectionHTML
   * @param {boolean} [isEmpty] - Whether Shopify confirmed that the cart is empty.
   */
  async #renderConfirmedCartSection(sectionHTML, isEmpty = false) {
    const options = {
      cache: false,
      mode: this.isDrawer ? 'hydration' : 'full',
    };

    if (sectionHTML) {
      try {
        await morphSection(this.sectionId, sectionHTML, options);
        document
          .getElementById(`shopify-section-${this.sectionId}`)
          ?.removeAttribute('data-cart-needs-refresh');
        this.#restoreDrawerFocus();
        return;
      } catch (error) {
        console.warn('[cart-items] Cart section response could not be applied; fetching a fresh copy:', error);
      }
    }

    // A confirmed empty cart can use the native template if Shopify omits its section.
    // Keep the drawer, close control and removal notice alive while replacing its items.
    if (this.isDrawer && isEmpty) {
      const drawer = this.closest('cart-drawer-component');
      const template = drawer?.querySelector('#empty-cart-template');
      const inner = drawer?.querySelector('.cart-drawer__inner');
      const closeButton = drawer?.querySelector('.theme-drawer__close-button');
      if (template instanceof HTMLTemplateElement && inner) {
        drawer.querySelector('.theme-drawer__header .theme-drawer__title')?.remove();
        inner.classList.add('cart-drawer--empty');
        closeButton?.classList.add('cart-drawer__close-button--empty');
        this.replaceChildren(template.content.cloneNode(true));
        document
          .getElementById(`shopify-section-${this.sectionId}`)
          ?.removeAttribute('data-cart-needs-refresh');
        this.#restoreDrawerFocus();
        return;
      }
    }

    let lastError;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await sectionRenderer.renderSection(this.sectionId, options);
        document
          .getElementById(`shopify-section-${this.sectionId}`)
          ?.removeAttribute('data-cart-needs-refresh');
        this.#restoreDrawerFocus();
        return;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Cart section could not be refreshed');
  }

  /**
   * Re-rendering removes the control that had focus (for example, the last
   * remove button). Keep keyboard users inside the open drawer.
   */
  #restoreDrawerFocus() {
    if (!this.isDrawer) return;
    const themeDrawer = this.closest('theme-drawer');
    if (!themeDrawer?.hasAttribute('open')) return;
    const active = document.activeElement;
    if (active && active !== document.body && themeDrawer.contains(active)) return;
    const closeButton = themeDrawer.querySelector('.theme-drawer__close-button');
    if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
  }

  /** @param {Object} removedItem */
  #announceRemovedItem(removedItem) {
    const sellingPlanId = removedItem.selling_plan_allocation?.selling_plan?.id;
    const restoreItem = {
      id: removedItem.id,
      quantity: removedItem.quantity,
      properties: removedItem.properties ?? {},
    };

    if (sellingPlanId) restoreItem.selling_plan = sellingPlanId;

    document.dispatchEvent(
      new CustomEvent('cart:item-removed', {
        detail: {
          item: restoreItem,
          title: removedItem.product_title || removedItem.title || '',
        },
      })
    );
  }

  #markSectionRefreshNeeded() {
    document
      .getElementById(`shopify-section-${this.sectionId}`)
      ?.setAttribute('data-cart-needs-refresh', '');
  }

  /** @param {number} line @param {Error} error */
  #handleCartRefreshError(line, error) {
    const message = this.dataset.refreshError || 'El carrito se actualizó, pero esta vista no pudo refrescarse.';
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const cartItemRow = this.refs.cartItemRows[line - 1];

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;
    cartItemRow?.classList.remove('removing');
    cartItemRow?.style.removeProperty('--row-height');

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = wasEmptyCartDrawer
            ? new Set()
            : new Set(
                Array.from(this.querySelectorAll('.cart-items__table-row[data-key]')).map((row) => row.dataset.key)
              );

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Highlight every new line, including items restored through Undo.
          const updatedComponent = Array.from(document.querySelectorAll('cart-items-component')).find(
            (component) => component instanceof HTMLElement && component.dataset.sectionId === this.sectionId
          );
          const updatedRows = updatedComponent?.querySelectorAll('.cart-items__table-row[data-key]') ?? [];

          for (const row of updatedRows) {
            if (!existingKeys.has(row.dataset.key)) {
              row.classList.add('cart-items__table-row--just-added');
              window.setTimeout(() => row.classList.remove('cart-items__table-row--just-added'), 1800);

              if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.warn('[cart-items] Event promise rejected:', error);
        // The cart changed but its view could not be refreshed; cart-drawer reconciles on next open.
        if (this.isDrawer) {
          document.getElementById(`shopify-section-${this.sectionId}`)?.setAttribute('data-cart-needs-refresh', '');
        }
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems(line) {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    this.refs.cartItemRows[line - 1]?.classList.add('cart-items__table-row--pending');

    for (const control of this.querySelectorAll('button, input, select')) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLButtonElement || control instanceof HTMLSelectElement)) {
        continue;
      }
      if (!this.#disabledControls.has(control)) this.#disabledControls.set(control, control.disabled);
      control.disabled = true;
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    this.querySelectorAll('.cart-items__table-row--pending').forEach((row) =>
      row.classList.remove('cart-items__table-row--pending')
    );

    for (const [control, wasDisabled] of this.#disabledControls) {
      if (control.isConnected) control.disabled = wasDisabled;
    }
    this.#disabledControls.clear();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
